import { countryFromISO } from "@/utils/country";
import { currencyFromISO, getAllCurrency } from "@/utils/currency";
import {
  Carrier,
  CarrierPricings,
  ShopDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@babackapp/ui/components/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { cloneDeep } from "lodash";
import { ArrowRightIcon } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

const PricingFormSchema = z.object({
  id: z.string(),
  origin: z.string(),
  destination: z.string(),
  price: z.coerce.number(),
  currency: z.string(),
});

export function PricingEdition({
  carrier,
  pricing,
  onSubmit,
  shop,
  setEditingIsOpen,
  setEditingPricingIndex,
}: {
  carrier: Carrier;
  pricing?: CarrierPricings;
  onSubmit: (params: {
    pricings: CarrierPricings[];
    carrierId: string;
  }) => void;
  shop: ShopDto;
  setEditingIsOpen: (value: boolean) => void;
  setEditingPricingIndex?: (index: number) => void;
}) {
  const t = useTranslation();
  const { handleSubmit, control } = useForm({
    defaultValues: {
      id: pricing ? pricing.id : uuidv4(),
      origin: pricing ? pricing.origin : carrier.originAccepted[0],
      destination: pricing
        ? pricing.destination
        : carrier.destinationAccepted[0],
      price: pricing ? pricing.price : 0,
      currency: pricing ? pricing.currency : shop.preferences.currency,
    },
    resolver: zodResolver(PricingFormSchema),
  });

  const submit = (data: CarrierPricings) => {
    const newCarriers = cloneDeep(carrier);
    if (pricing) {
      const index = carrier.pricings.indexOf(pricing);
      newCarriers.pricings[index] = data;
    } else {
      newCarriers.pricings.push(data);
    }
    setEditingIsOpen(false);
    if (setEditingPricingIndex) setEditingPricingIndex(-1);
    onSubmit({
      pricings: newCarriers.pricings,
      carrierId: carrier.id,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="py-2">
      <div className="flex space-x-4 items-center">
        <Controller
          name="origin"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>{countryFromISO(field.value)}</SelectTrigger>
              <SelectContent>
                {carrier.originAccepted.map((origin) => (
                  <SelectItem key={origin} value={origin}>
                    {countryFromISO(origin)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <div>
          <ArrowRightIcon className="h-4 w-4 text-gray-400" />
        </div>
        <Controller
          name="destination"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>{countryFromISO(field.value)}</SelectTrigger>
              <SelectContent>
                {carrier.destinationAccepted.map((destination) => (
                  <SelectItem key={destination} value={destination}>
                    {countryFromISO(destination)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <Controller
          name="price"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              className="w-32"
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
        <Controller
          name="currency"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>{field.value}</SelectTrigger>
              <SelectContent>
                {Object.keys(getAllCurrency()).map((currency) => (
                  <SelectItem key={currency} value={currency}>
                    {currencyFromISO(currency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <Button type="submit" variant="default" size="sm">
          {pricing ? t("misc.update") : t("misc.add")}
        </Button>
      </div>
    </form>
  );
}
